<script setup>
import ArgonButton from "@/components/ArgonButton.vue";
import { ref, onMounted } from 'vue';
import axios from '../axios';
import store from '../store';

const signature_domestic = ref(0);
const signature_international = ref(0);
const insurance_international = ref(0);
const insurance_domestic = ref(0);
const area_surcharge = ref(0);
const additional_surcharge = ref(0);
const cms_fee = ref(0);
const pickup_charge = ref(0);
const isLoading = ref(true);

const tiers = ref([]);

const fetchTiers = () => {
  axios.post('api/InsuranceRateTiers')
    .then(response => {
      tiers.value = response.data;
    })
    .catch(error => console.error('Error fetching tiers:', error));
};



const tierDeleteId = ref(null);
const tierToDelete = ref(null);
const showProceedModel = ref(false);

const deleteTier = (tier, index) => {
  if (!tier.id) {
    tiers.value.splice(index, 1);
    return;
  }
  tierDeleteId.value = tier.id;
  tierToDelete.value = tier;
  showProceedModel.value = true;
};

const confirmDeleteTier = () => {
  axios.post('api/deleteInsuranceRateTier', { id: tierDeleteId.value })
    .then(() => {
      store.commit('setSuccessNotification', "Tier deleted successfully");
      setTimeout(() => store.commit('clearNotifications'), 5000);
      fetchTiers();
      showProceedModel.value = false;
      tierDeleteId.value = null;
      tierToDelete.value = null;
    })
    .catch(() => {
      store.commit('setErrorNotification', "Error deleting tier");
      setTimeout(() => store.commit('clearNotifications'), 5000);
      showProceedModel.value = false;
      tierDeleteId.value = null;
      tierToDelete.value = null;
    });
};

const addNewInlineTier = () => {
  tiers.value.push({
    id: null,
    min_value: '',
    max_value: '',
    domestic_rate: '',
    international_rate: ''
  });
};

const saveAllData = () => {
  // Save configurations
  const configurationData = {
    cms_fee: cms_fee.value,
    pickup_charge: pickup_charge.value,
    area_surcharge: area_surcharge.value,
    additional_surcharge: additional_surcharge.value,
    signature_domestic: signature_domestic.value,
    signature_international: signature_international.value,
    insurance_international: insurance_international.value,
    insurance_domestic: insurance_domestic.value
  };

  const configPromise = axios.post('api/saveConfigurationOfBoxes', configurationData);

  // Save Tiers in bulk
  const tiersPromise = axios.post('api/saveAllInsuranceRateTiers', {
    tiers: tiers.value
  });

  Promise.all([configPromise, tiersPromise])
    .then(() => {
      fetchTiers();
      store.commit('setSuccessNotification', 'All configurations and tiers saved successfully');
      store.commit('setErrorNotification', null);
      setTimeout(() => store.commit('clearNotifications'), 5000);
    })
    .catch(error => {
      let errorMsg = 'Error saving configurations or tiers';
      if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.response?.data?.errors) {
        errorMsg = Object.values(error.response.data.errors).flat().join(', ');
      }
      store.commit('setErrorNotification', errorMsg);
      store.commit('setSuccessNotification', null);
      setTimeout(() => store.commit('clearNotifications'), 5000);
      console.error('Error saving all data:', error);
    });
};



const number = (evt) => {
  const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
  if (allowedKeys.includes(evt.key)) return;

  // Allow Ctrl/Cmd shortcuts (copy/paste/select-all etc.)
  if (evt.ctrlKey || evt.metaKey) return;

  const input = evt.target;
  if (!input) return;

  // Allow one decimal dot only
  if (evt.key === '.') {
    if (String(input.value).includes('.')) {
      evt.preventDefault();
    }
    return;
  }

  // Allow only 2 digits after decimal point
  if (/^\d$/.test(evt.key)) {
    const value = String(input.value ?? '');
    const dotIndex = value.indexOf('.');
    if (dotIndex !== -1) {
      const selectionStart = input.selectionStart ?? value.length;
      const selectionEnd = input.selectionEnd ?? value.length;
      const decimals = value.slice(dotIndex + 1);
      const isEditingBeforeOrOnDot = selectionStart <= dotIndex;
      const replacingSelection = selectionEnd > selectionStart;
      if (!isEditingBeforeOrOnDot && !replacingSelection && decimals.length >= 2) {
        evt.preventDefault();
      }
    }
    return;
  }

  evt.preventDefault();
};

// saveAllData replaces saveConfiguration

onMounted(() => {
  fetchTiers();
  axios.post('api/getConfigurationOfBoxes')
    .then(response => {

      cms_fee.value = response.data.cms_fee;
      pickup_charge.value = response.data.pickup_charge;
      area_surcharge.value = response.data.area_surcharge;
      additional_surcharge.value = response.data.additional_surcharge;

      signature_domestic.value = response.data.signature_domestic;
     
      signature_international.value = response.data.signature_international;
     
      insurance_international.value = response.data.insurance_international;
      insurance_domestic.value = response.data.insurance_domestic;
      isLoading.value = false;
    })
    .catch(error => {
      isLoading.value = false;
      console.error('Error fetching configurations:', error);
    });
});
</script>

<template>
  <div class="py-4 ps-1 container-fluid overflow-hidden">

    <div class="row py-4 mt-5">

      <div class="col-md-12">
        <div class="card p-3 p-md-5 shadow-lg rounded-xl bg-light-color text-gray-800 position-relative">
                    <div class="ctm-loader" v-if="isLoading">
                        <div class="loader" id="loader-5">
                            <span></span>
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                    </div>
                    
       

        <div class="card">
          <div class="card-header pb-0">
            <h6 class="mb-0 text-dark font-weight-bold">Add Configurations</h6>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="example-text-input" class="form-control-label">Signature for Domestic</label>
                <input class="form-control" type="text" v-model="signature_domestic" @keydown="number" />
              </div>
              <div class="col-md-6 mb-3">
                <label for="example-text-input" class="form-control-label">Signature for International</label>
                <input class="form-control" type="text" v-model="signature_international"  @keydown="number"/>
              </div>
              <!-- <div class="col-md-6 mb-3">
                <label for="example-text-input" class="form-control-label">Domestic insurance ($ per $100 over first $100)</label>
                <input class="form-control" type="text" v-model="insurance_domestic" @keydown="number"/>
              </div>
              <div class="col-md-6 mb-3">
                <label for="example-text-input" class="form-control-label">International insurance ($ per $100 over first $100)</label>
                <input class="form-control" type="text" v-model="insurance_international" @keydown="number"/>
              </div> -->
              <div class="col-md-4 mb-3">
                <label for="example-text-input" class="form-control-label">Area Surcharge</label>
                <input class="form-control" type="text" v-model="area_surcharge" @keydown="number" />
              </div>
              <div class="col-md-4 mb-3">
                <label for="example-text-input" class="form-control-label">Additional Surcharge</label>
                <input class="form-control" type="text" v-model="additional_surcharge" @keydown="number" />
              </div>
              <div class="col-md-4 mb-3">
                <label for="example-text-input" class="form-control-label">CMS Processing Fees</label>
                <input class="form-control" type="text" v-model="cms_fee" @keydown="number"/>
              </div>
              <!-- <div class="col-md-6 mb-3">
                <label for="example-text-input" class="form-control-label">Pickup Charge</label>
                <input class="form-control" type="text" v-model="pickup_charge" @keydown="number"/>
              </div> -->
            </div>
            <!-- Removed extra save button -->


          </div>
        </div>
         <div class="card mb-4">
          <div class="card-header pb-0 d-flex justify-content-between align-items-center">
            <div>
              <h6 class="mb-0 text-dark font-weight-bold">Insurance Rate Tiers</h6>
            </div>
            <argon-button size="sm" class="btn-bg text-white mb-0" @click="addNewInlineTier">+ Add New Tier</argon-button>
          </div>
          <div class="card-body">
            <div class="mb-4">
              <table class="table align-items-center mb-0" style="table-layout: fixed; width: 100%;">
                <thead>
                  <tr>
                    <th class="text-sm font-weight-bold text-dark" style="white-space: nowrap; width: 120px;">Sr. No.</th>
                    <th class="text-sm font-weight-bold text-dark" style="white-space: normal; width: 260px;">Declared Value Range (USD)</th>
                    <th class="text-sm font-weight-bold text-dark" style="white-space: normal;">Domestic Insurance<br/>($ per $100)</th>
                    <th class="text-sm font-weight-bold text-dark" style="white-space: normal;">International Insurance<br/>($ per $100)</th>
                    <th class="text-sm font-weight-bold text-dark text-center" style="white-space: nowrap; width: 120px;">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(tier, index) in tiers" :key="index">
                    <td><p class="text-sm font-weight-bold mb-0 px-3">{{ index + 1 }}</p></td>
                    <td>
                      <div class="d-flex align-items-center">
                        <input class="form-control form-control-sm text-center" style="width: 100px;" type="text" v-model="tier.min_value" @keydown="number" />
                        <span class="mx-3 text-sm text-secondary">-</span>
                        <input class="form-control form-control-sm text-center" style="width: 100px;" type="text" v-model="tier.max_value" @keydown="number" />
                      </div>
                    </td>
                    <td>
                      <div class="d-flex px-3">
                        <input class="form-control form-control-sm text-center" style="width: 100px;" type="text" v-model="tier.domestic_rate" @keydown="number" />
                      </div>
                    </td>
                    <td>
                      <div class="d-flex px-3">
                        <input class="form-control form-control-sm text-center" style="width: 100px;" type="text" v-model="tier.international_rate" @keydown="number" />
                      </div>
                    </td>
                    <td class="px-3 text-center">
                      <button class="delete-notification" @click="deleteTier(tier, index)">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-trash3" viewBox="0 0 16 16">
                          <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5M11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47M8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="tiers.length === 0">
                    <td colspan="5" class="text-center text-sm">No tiers available.</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div class="row">
              <div class="col-12 text-end pt-3">
                <argon-button size="sm" class="ms-auto btn-bg text-white" @click="saveAllData">Save</argon-button>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  </div>

    <div v-if="showProceedModel" class="modal-overlay">
        <div class="modal-container">
            <div class="modal-header">
                <h5 class="modal-title">Confirmation</h5>
                <button class="close-button" @click="showProceedModel = false">&times;</button>
            </div>
            <div class="modal-body">
                <p class="mb-0">Are you sure you want to delete the tier <b>${{ tierToDelete?.min_value }} - ${{ tierToDelete?.max_value }}</b>?</p>
            </div>
            <div class="modal-footer">
                <button class="btn btn-bg-yellow" @click="showProceedModel = false">No</button>
                <button class="btn btn-bg text-white" @click="confirmDeleteTier">Yes</button>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
}

.modal-container {
    background: white;
    padding: 20px;
    border-radius: 8px;
    width: 400px;
    max-width: 90%;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-body {
    margin-top: 10px;
    margin-bottom: 10px;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
}

.close-button {
    background: transparent;
    border: none;
    font-size: 24px;
    cursor: pointer;
}
</style>
